import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Box, Grid } from '@material-ui/core';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@this/components/shared/ui/data_displays/table';
import { Link } from '@this/components/shared/ui/navigations/link';
import { Title } from '@this/components/admin/admin';

import axios from 'axios';
import SimpleLoading from '@this/shared/simple_loading/simple_loading';
import ManualModal from '@this/components/admin/manuals/manual_modal';

interface ManualJson {
  id: number;
  name: string;
  file_url: string;
}

interface Response {
  manuals: ManualJson[];
}

const Manuals = observer(() => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [manuals, setManuals] = useState<ManualJson[]>([]);
  const [manual, setManual] = useState<ManualJson | null>(null);
  const [showManualModal, setShowManualModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>('');

  const fetchManuals = useCallback(async () => {
    setIsLoading(true);
    axios.get<Response>('/admin/manuals.json', {}).then(response => {
      setIsLoading(false);
      setManuals(response.data.manuals);
    });
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchManuals();
  }, []);

  return (
    <>
      <Title>マニュアル</Title>
      <div style={styles.content}>
        {isLoading ? (
          <SimpleLoading />
        ) : (
          <Grid item xs={12}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {manuals.map(data => (
                  <TableRow hover key={data.id} data-e2e-id={data.id}>
                    <TableCell className="e2e-name">
                      <span style={styles.text}>{data.name}</span>
                    </TableCell>
                    <TableCell>
                      <Link target="_blank" href={`${data.file_url}`} isExternal>
                        <span style={styles.linkText}>表示</span>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        size="small"
                        onClick={() => {
                          setManual(data);
                          setShowManualModal(true);
                          setModalType('mod');
                        }}
                        disabled={isLoading}
                      >
                        <span style={styles.linkText}>上書き</span>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        size="small"
                        onClick={() => {
                          setManual(data);
                          setShowManualModal(true);
                          setModalType('delete');
                        }}
                        disabled={isLoading}
                      >
                        <span style={styles.linkText}>削除</span>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box marginTop="10px">
              <Link
                onClick={() => {
                  setManual(null);
                  setShowManualModal(true);
                  setModalType('add');
                }}
                disabled={isLoading}
              >
                <span style={styles.linkText}>＋追加</span>
              </Link>
            </Box>

            {showManualModal && (
              <ManualModal
                open
                onClose={() => setShowManualModal(false)}
                afterSubmit={() => fetchManuals()}
                id={manual?.id}
                fileName={manual?.name}
                modalType={modalType}
              />
            )}
          </Grid>
        )}
      </div>
    </>
  );
});

const getStyles = (theme: AITTheme) => ({
  text: {
    padding: 3,
    fontSize: 12,
    fontWeight: 'bold' as const,
    color: theme.textColor
  },
  linkText: {
    padding: 3,
    fontSize: 12,
    fontWeight: 'bold' as const,
    color: theme.linkColor
  },
  content: {
    padding: '10px 20px'
  }
});

export default Manuals;
